<template>
    <div class="color-selection">
        <div class="color-selection__car-image">
            <div v-if="imageSrc" class="color-selection__car-image-wrapper tw-max-w-[680px] tw-mx-auto">
                <figure class="aspect-ratio">
                    <img
                        class="width-100%"
                        :src="imageSrc"
                        :alt="activeColor && activeColor.description"
                        height="auto"
                    >
                </figure>
            </div>
        </div>
        <div class="m-top--l color-selection__colors tw-text-center">
            <loading-wrapper v-if="colors.length" :loading="loading">
                <dk-color-picker
                    :colors="colors"
                    :max-items="colorItemsToShow"
                    size="s-vdp"
                    :is-three-steps-dk-promo="isVdpEcommerce"
                    :is-vdp-ecommerce="isVdpEcommerce"
                    :max-landing="isVdpEcommerce ? 6 : undefined"
                    @item-click="onColorSelect"
                />
            </loading-wrapper>
            <div
                v-else
                class="color-selection__basic-colors tw-grid tw-grid-cols-3 max-lg:tw-px-4 tw-gap-4 lg:tw-flex lg:tw-items-center lg:tw-justify-center lg:tw-gap-14"
            >
                <label
                    v-for="(color, i) in ['black', 'white', 'red', 'gray', 'blue', 'others']"
                    :key="i"
                    class="d--inline-flex items-center m-right--xs"
                >
                    <input
                        v-model="smokeColor"
                        class="m--0"
                        type="radio"
                        name="smoke-color"
                        :value="$t(`configure-car.smoke-color.${color}`)"
                    >
                    <span class="ts--caption color--grey-900 m-left--xxs">
                        {{ $t(`configure-car.smoke-color.${color}`) }}
                    </span>
                </label>
            </div>
        </div>
    </div>
</template>

<script>
import { fetchConfigurator } from '@v4/services/api/v3/car';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';

export default {
    name: 'DkColorSelection',
    components: {
        LoadingWrapper: () => ({
            component: import(
                /* webpackChunkName: "LoadingWrapper-vue" */ '@v4/common/components/atoms/LoadingWrapper'
            ),
        }),
        DkColorPicker: () => ({
            component: import(
                /* webpackChunkName: "ColorSelector-vue" */ '@v4/common/components/molecules/DkColorPicker.vue'
            ),
        }),
    },
    mixins: [gaEventHandler],
    props: {
        /**
         * Make name
         */
        make: {
            type: String,
            default: '',
        },
        /**
         * Model name
         */
        model: {
            type: String,
            default: '',
        },
        isVdpWithWizardForm: {
            type: Boolean,
            default: false,
        },
        isVdpEcommerce: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            colorImage: '',
            error: null,
            colors: [],
            activeColor: {},
            smokeColor: '',
            colorsMaxItem: 4,
        };
    },
    computed: {
        imageSrc() {
            return (
                (this.activeColor && this.activeColor.colorImage && this.activeColor.colorImage.image800) ||
                this.colorImage ||
                ''
            );
        },
        colorItemsToShow() {
            if (this.colors.length === this.colorsMaxItem + 1) {
                return this.colors.length;
            }

            return this.colorsMaxItem;
        },
    },
    mounted() {
        this.fetchCarConfiguration();
    },
    methods: {
        fetchCarConfiguration() {
            this.loading = true;
            fetchConfigurator(this.make, this.model)
                .then(({ data }) => {
                    this.colors = ((data.colors && data.colors.external) || []).map((color) => {
                        if (color.active) {
                            this.activeColor = color;
                            this.$emit('initial-color-select', this.activeColor);
                        }
                        return color;
                    });

                    this.colorImage = data.image800 || data.imageOriginal;
                    this.loading = false;
                })
                .catch((error) => {
                    this.error = error;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onColorSelect(index = 0) {
            const colors = this.colors;
            if (!colors[index]) {
                return;
            }

            this.colors = colors.map((item, i) => {
                if (i === index) {
                    item.active = true;
                } else {
                    item.active = false;
                }
                return item;
            });

            this.activeColor = colors[index];

            this.pushGaEvent(
                'vdp-color selection',
                `click on a color ${this.activeColor.description} (new VDP)`,
                'Select'
            );

            if (this.isVdpWithWizardForm) {
                this.$emit('color-select', this.activeColor);
            }
        },
    },
};
</script>

<style lang="scss" src="@v4/vdp/components/molecules/ColorSelection/_index.scss"></style>
